/**
 * Project detail page component for viewing a specific OpenStack project with VM list.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import {
  getProject,
  refreshProject,
  listProjectVMs,
  refreshProjectVMs,
  getCloud,
  OpenStackProject,
  OpenStackVM,
  OpenStackCloud,
  listCloudShares,
  addCloudShare,
  removeCloudShare,
  CloudShareList,
  CloudShareUser,
  listProjectClusters,
  KubernetesCluster,
} from '../api/openstack';
import { searchUsers, UserListResponse } from '../api/users';
import { RefreshIcon, PlayIcon, StopIcon, PauseIcon, TrashIcon } from '../components/Icons';
import { QuotaGauge } from '../components/QuotaGauge';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { logError } from '../utils/errorLogging';
import { getErrorWithContext } from '../utils/errorMessages';
import './ProjectDetailPage.css';

export const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<OpenStackProject | null>(null);
  const [cloud, setCloud] = useState<OpenStackCloud | null>(null);
  const [vms, setVms] = useState<OpenStackVM[]>([]);
  const [clusters, setClusters] = useState<KubernetesCluster[]>([]);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof OpenStackVM | ''>('vm_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Sharing state
  const [shareData, setShareData] = useState<CloudShareList | null>(null);
  const [loadingShares, setLoadingShares] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState<UserListResponse[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [addingShare, setAddingShare] = useState(false);
  const [removingShare, setRemovingShare] = useState<number | null>(null); // user_id being removed
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const highlightedItemRef = useRef<HTMLDivElement>(null);

  const { isConnected, on, off } = useWebSocket();
  const { user } = useAuth();
  const { settings } = useSettings();

  const loadProject = async () => {
    if (!projectId) return;
    
    try {
      setError(null);
      const projectData = await getProject(projectId);
      setProject(projectData);
      
      // Load cloud information to get cloud_name for title
      if (projectData.cloud_id) {
        try {
          const cloudData = await getCloud(projectData.cloud_id);
          setCloud(cloudData);
        } catch (err: any) {
          logError('Failed to load cloud', err);
          // Don't fail the whole page if cloud load fails
        }
      }
    } catch (err: any) {
      logError('Failed to load project', err);
      setError(getErrorWithContext(err, 'load project'));
    }
  };

  const loadVMs = async () => {
    if (!projectId) return;
    
    try {
      setError(null);
      const vmData = await listProjectVMs(projectId);
      setVms(vmData.vms);
    } catch (err: any) {
      logError('Failed to load VMs', err);
      setError(getErrorWithContext(err, 'load VMs'));
    }
  };

  const loadClusters = async () => {
    if (!projectId) return;
    
    // Only load clusters if detection is enabled
    if (!settings?.kubernetes_cluster_detection_enabled) {
      setClusters([]);
      return;
    }
    
    try {
      setLoadingClusters(true);
      const clusterData = await listProjectClusters(projectId);
      setClusters(clusterData.clusters);
    } catch (err: any) {
      logError('Failed to load clusters', err);
      // Don't show error for clusters - it's not critical
      setClusters([]);
    } finally {
      setLoadingClusters(false);
    }
  };

  const loadShares = async () => {
    if (!cloud?.id) return;
    
    try {
      setLoadingShares(true);
      const shares = await listCloudShares(cloud.id);
      setShareData(shares);
    } catch (err: any) {
      logError('Failed to load shares', err);
      // Don't show error for shares - it's not critical
    } finally {
      setLoadingShares(false);
    }
  };

  const loadAvailableUsers = useCallback(async () => {
    if (!userSearchTerm || userSearchTerm.length < 2) {
      setAvailableUsers([]);
      setShowUserDropdown(false);
      setHighlightedIndex(-1);
      return;
    }
    
    try {
      const users = await searchUsers(userSearchTerm, 50);
      // Filter out owner and already shared users
      const filteredUsers = users.filter((user) => {
        // Don't show the owner
        if (cloud?.user_id === user.id) {
          return false;
        }
        // Don't show users already shared
        return !shareData?.shared_users.some(
          (su) => su.user_id === user.id
        );
      });
      setAvailableUsers(filteredUsers);
      // Show dropdown if we have results
      if (filteredUsers.length > 0) {
        setShowUserDropdown(true);
        setHighlightedIndex(-1); // Reset highlight when new results
      } else {
        setShowUserDropdown(false);
        setHighlightedIndex(-1);
      }
    } catch (err: any) {
      logError('Failed to search users', err);
      setError(getErrorWithContext(err, 'search users'));
      setAvailableUsers([]);
      setShowUserDropdown(false);
      setHighlightedIndex(-1);
    }
  }, [userSearchTerm, cloud?.user_id, shareData?.shared_users]);

  const handleAddShare = async (userId: number) => {
    if (!cloud?.id) return;
    
    try {
      setAddingShare(true);
      setError(null);
      await addCloudShare(cloud.id, userId);
      await loadShares();
      setUserSearchTerm('');
      setShowUserDropdown(false);
      setSuccess('User added successfully');
    } catch (err: any) {
      logError('Failed to add share', err);
      setError(getErrorWithContext(err, 'add user to share'));
    } finally {
      setAddingShare(false);
    }
  };

  const handleRemoveShare = async (userId: number) => {
    if (!cloud?.id) return;
    
    setRemovingShare(userId);
    try {
      setError(null);
      await removeCloudShare(cloud.id, userId);
      await loadShares();
      setSuccess('User removed successfully');
    } catch (err: any) {
      logError('Failed to remove share', err);
      setError(getErrorWithContext(err, 'remove user from share'));
    } finally {
      setRemovingShare(null);
    }
  };

  const handleRefresh = async () => {
    if (!projectId) return;
    
    setRefreshing(true);
    setError(null);
    setSuccess(null);
    
    try {
      await refreshProject(projectId);
      await refreshProjectVMs(projectId);
      await loadProject();
      await loadVMs();
      await loadClusters();
      setSuccess('Project and VMs refreshed successfully');
    } catch (err: any) {
      logError('Failed to refresh', err);
      setError(getErrorWithContext(err, 'refresh project'));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      setLoading(true);
      Promise.all([loadProject(), loadVMs(), loadClusters()]).finally(() => {
        setLoading(false);
      });
    }
  }, [projectId]);

  useEffect(() => {
    if (cloud?.id) {
      loadShares();
    }
  }, [cloud?.id]);

  // Reload clusters when settings change (cluster detection enabled/disabled)
  useEffect(() => {
    if (projectId) {
      loadClusters();
    }
  }, [settings?.kubernetes_cluster_detection_enabled, projectId]);

  useEffect(() => {
    if (userSearchTerm && userSearchTerm.length >= 2) {
      const timeoutId = setTimeout(() => {
        loadAvailableUsers();
      }, 300); // Debounce search
      
      return () => clearTimeout(timeoutId);
    } else {
      setAvailableUsers([]);
      setShowUserDropdown(false);
    }
  }, [userSearchTerm, loadAvailableUsers]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && highlightedItemRef.current) {
      highlightedItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex]);

  // Register WebSocket event handlers for VM updates
  useEffect(() => {
    const handleVMListUpdate = async (message: { event: string; data?: any }) => {
      const { project_id, vm_data } = message.data || {};
      
      if (project_id === projectId) {
        // Full VM list update - replace entire list
        if (vm_data && Array.isArray(vm_data)) {
          // Replace entire VM list with the new data
          setVms(vm_data);
          // Reload clusters when VMs are updated
          await loadClusters();
        } else {
          // Fallback: reload VMs if data not included in message
          await loadVMs();
          await loadClusters();
        }
      }
    };

    const handleVMUpdate = async (message: { event: string; data?: any }) => {
      const { event_type, project_id } = message.data || {};
      
      if (project_id === projectId) {
        if (event_type === 'updated') {
          // Individual VM update - reload to get latest state
          await loadVMs();
          await loadClusters();
        }
      }
    };

    const handleProjectUpdate = async (message: { event: string; data?: any }) => {
      const { event_type, cloud_id, project_data } = message.data || {};
      
      if (event_type === 'deleted') {
        // Cloud was deleted (or share was removed) - check if it's the current project's cloud
        if (cloud_id === cloud?.id) {
          // Redirect to projects page
          navigate('/projects');
        }
      } else if (event_type === 'updated' && project_data && Array.isArray(project_data)) {
        // Check if this project was updated
        const updatedProject = project_data.find(
          (p: OpenStackProject) => p.project_id === projectId
        );
        if (updatedProject) {
          setProject(updatedProject);
        }
      }
    };

    const unsubscribeVMList = on('vm_list_update', handleVMListUpdate);
    const unsubscribeVM = on('vm_update', handleVMUpdate);
    const unsubscribeProject = on('project_update', handleProjectUpdate);

    return () => {
      unsubscribeVMList();
      unsubscribeVM();
      unsubscribeProject();
    };
  }, [projectId, cloud?.id, navigate, on, off, loadVMs]);

  const formatBytes = (mb: number): string => {
    if (mb < 1024) {
      return `${mb} MB`;
    }
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatRelativeTime = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    
    // Parse the UTC date string and convert to local time
    let dateStr = dateString.trim();
    
    // Check if the string has timezone information (Z, +HH:MM, or -HH:MM)
    const hasTimezone = dateStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateStr);
    
    if (!hasTimezone) {
      // No timezone info - assume UTC and append 'Z'
      dateStr = dateStr.replace(/\s+$/, '');
      if (dateStr.includes('T')) {
        dateStr = dateStr + 'Z';
      } else {
        dateStr = dateStr + 'T00:00:00Z';
      }
    }
    
    const date = new Date(dateStr);
    
    // Verify the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSeconds < 60) {
      return `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''} ago`;
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      // For longer periods, show the date
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getStatusColor = (status?: string): string => {
    if (!status) return 'var(--text-secondary)';
    const upperStatus = status.toUpperCase();
    if (upperStatus === 'ACTIVE') return 'var(--success-color)';
    if (upperStatus === 'SHUTOFF' || upperStatus === 'STOPPED') return 'var(--text-secondary)';
    if (upperStatus === 'PAUSED' || upperStatus === 'SUSPENDED') return 'var(--warning-color)';
    if (upperStatus === 'ERROR' || upperStatus === 'FAILED') return 'var(--error-color)';
    return 'var(--text-color)';
  };

  const shouldShowStartButton = (status?: string): boolean => {
    if (!status) return true;
    const upperStatus = status.toUpperCase();
    // Show start button if VM is not active (shutoff, stopped, paused, etc.)
    return upperStatus !== 'ACTIVE';
  };

  const shouldShowStopPauseButtons = (status?: string): boolean => {
    if (!status) return false;
    const upperStatus = status.toUpperCase();
    // Show stop/pause buttons only if VM is active
    return upperStatus === 'ACTIVE';
  };

  // Filter VMs based on search term
  const filteredVMs = vms.filter(vm => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (vm.vm_name || '').toLowerCase().includes(searchLower) ||
      (vm.vm_id || '').toLowerCase().includes(searchLower) ||
      (vm.status || '').toLowerCase().includes(searchLower) ||
      (vm.created_by_user_name || '').toLowerCase().includes(searchLower) ||
      (vm.created_by_user_id || '').toLowerCase().includes(searchLower) ||
      (vm.flavor_name || '').toLowerCase().includes(searchLower)
    );
  });

  // Sort filtered VMs
  const sortedVMs = [...filteredVMs].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue: any = a[sortColumn];
    let bValue: any = b[sortColumn];

    // Handle different data types
    if (sortColumn === 'created_at') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    } else if (sortColumn === 'cores' || sortColumn === 'ram_mb' || sortColumn === 'disk_gb') {
      aValue = aValue ?? 0;
      bValue = bValue ?? 0;
    } else if (typeof aValue === 'string') {
      aValue = (aValue || '').toLowerCase();
      bValue = (bValue || '').toLowerCase();
    } else {
      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (column: keyof OpenStackVM) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="project-detail-page">
          <div className="project-detail-container">
            <div className="loading">Loading project details...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !project) {
    return (
      <Layout>
        <div className="project-detail-page">
          <div className="project-detail-container">
            <div className="project-breadcrumb">
              <Link to="/projects">Projects</Link>
              {projectId && (
                <>
                  <span className="project-breadcrumb-separator">/</span>
                  <span className="project-breadcrumb-current">
                    {cloud?.cloud_name || projectId}
                  </span>
                </>
              )}
            </div>
            <div className="error">{error}</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="project-detail-page">
        <div className="project-detail-container">
          <div className="project-breadcrumb">
            <Link to="/projects">Projects</Link>
            {projectId && (
              <>
                <span className="project-breadcrumb-separator">/</span>
                <span className="project-breadcrumb-current">
                  {cloud?.cloud_name || project?.project_name || projectId}
                </span>
              </>
            )}
          </div>
          <div className="project-detail-header">
          <div className="project-detail-title">
            <div className="project-detail-title-content">
              <h1 
                title={cloud?.cloud_name || project?.project_id || 'Project Details'}
              >
                {cloud?.cloud_name || project?.project_id || 'Project Details'}
              </h1>
              {project?.project_name && (
                <h3 
                  title={project.project_name}
                >
                  {project.project_name}
                </h3>
              )}
            </div>
          </div>
          <div className="project-detail-actions">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="btn-refresh"
              title="Refresh project and VMs"
            >
              <RefreshIcon />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {project && (
          <>
            {/* Project Statistics Section */}
            <div className="project-stats-section">
              <h2>Project Statistics</h2>
              <div className="project-stats-grid">
                <div className="project-stat-card">
                  <h3>Compute Resources</h3>
                  <div className="project-card-content">
                    <div className="project-card-quotas">
                      <QuotaGauge
                        label="Instances"
                        allocated={project.quotas.instances_allocated}
                        quota={project.quotas.instances_quota}
                      />
                      <QuotaGauge
                        label="VCPUs"
                        allocated={project.quotas.cores_allocated}
                        quota={project.quotas.cores_quota}
                      />
                      <QuotaGauge
                        label="RAM (GB)"
                        allocated={Math.round((project.quotas.memory_allocated || 0) / 1024)}
                        quota={project.quotas.memory_quota ? Math.round(project.quotas.memory_quota / 1024) : undefined}
                      />
                    </div>
                  </div>
                </div>

                <div className="project-stat-card">
                  <h3>Volume Resources</h3>
                  <div className="project-card-content">
                    <div className="project-card-quotas">
                      <QuotaGauge
                        label="Volumes"
                        allocated={project.quotas.volumes_allocated}
                        quota={project.quotas.volumes_quota}
                      />
                      <QuotaGauge
                        label="Volume Snapshots"
                        allocated={project.quotas.volume_snapshots_allocated}
                        quota={project.quotas.volume_snapshots_quota}
                      />
                      <QuotaGauge
                        label="Volume Storage (GB)"
                        allocated={project.quotas.volume_storage_allocated}
                        quota={project.quotas.volume_storage_quota}
                      />
                    </div>
                  </div>
                </div>

                <div className="project-stat-card">
                  <h3>Network Resources</h3>
                  <div className="project-card-content">
                    <div className="project-card-quotas">
                      <QuotaGauge
                        label="Floating IPs"
                        allocated={project.quotas.floating_ips_allocated}
                        quota={project.quotas.floating_ips_quota}
                      />
                      <QuotaGauge
                        label="Security Groups"
                        allocated={project.quotas.security_groups_allocated}
                        quota={project.quotas.security_groups_quota}
                      />
                      <QuotaGauge
                        label="Networks"
                        allocated={project.quotas.networks_allocated}
                        quota={project.quotas.networks_quota}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="project-stats-footer">
                {cloud && cloud.user_id && user && cloud.user_id !== user.id && (
                  <div className="project-owner-info">
                    Owner: {cloud.owner_name || 'Unknown'}
                    {cloud.owner_email && ` <${cloud.owner_email}>`}
                  </div>
                )}
                <div className="project-last-refreshed">
                  Last refreshed: {formatRelativeTime(project.last_refreshed)}
                </div>
              </div>
            </div>

            {/* Kubernetes Clusters Section - Only show if clusters exist and detection is enabled */}
            {settings?.kubernetes_cluster_detection_enabled && clusters.length > 0 && (
              <div className="project-clusters-section">
                <h2>Kubernetes Clusters</h2>
                <div className="project-clusters-grid">
                  {clusters.map((cluster) => {
                    // Check if any VMs in the cluster are not active
                    const hasInactiveVMs = cluster.vms.some(
                      (vm) => !vm.status || vm.status.toLowerCase() !== 'active'
                    );
                    
                    return (
                      <div
                        key={cluster.cluster_name}
                        className={`project-cluster-card ${hasInactiveVMs ? 'project-cluster-card-error' : ''}`}
                        onClick={() => navigate(`/projects/${projectId}/clusters/${encodeURIComponent(cluster.cluster_name)}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/projects/${projectId}/clusters/${encodeURIComponent(cluster.cluster_name)}`);
                          }
                        }}
                      >
                        <h3>{cluster.cluster_name}</h3>
                        <div className="project-cluster-stats">
                          <div className="project-cluster-stat">
                            <span className="project-cluster-stat-label">Control Planes:</span>
                            <span className="project-cluster-stat-value">{cluster.control_plane_count}</span>
                          </div>
                          <div className="project-cluster-stat">
                            <span className="project-cluster-stat-label">Workers:</span>
                            <span className="project-cluster-stat-value">{cluster.worker_count}</span>
                          </div>
                          <div className="project-cluster-stat">
                            <span className="project-cluster-stat-label">Total VMs:</span>
                            <span className="project-cluster-stat-value">{cluster.total_vms}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sharing Section - Only show if user is owner */}
            {cloud && shareData?.is_owner && (
              <div className="project-sharing-section">
                <h2>Sharing</h2>
                <div className="project-sharing-multiselect">
                  {/* Shared with section */}
                  <div className="project-sharing-shared-with">
                    <label className="project-sharing-label">Shared with</label>
                    <div className="project-sharing-shared-box">
                      {shareData.shared_users.length === 0 ? (
                        <div className="project-sharing-empty">No users shared yet</div>
                      ) : (
                        shareData.shared_users.map((sharedUser) => (
                          <div key={sharedUser.user_id} className="project-sharing-pill">
                            <span className="project-sharing-pill-text">
                              {sharedUser.name} ({sharedUser.email})
                            </span>
                            <button
                              className="project-sharing-pill-remove"
                              onClick={() => handleRemoveShare(sharedUser.user_id)}
                              title="Remove access"
                              aria-label="Remove access"
                              disabled={removingShare === sharedUser.user_id}
                            >
                              ×
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  
                  {/* Add user section */}
                  <div className="project-sharing-add-user">
                    <label className="project-sharing-label">Add user</label>
                    <div className="project-sharing-input-container">
                      <input
                        type="text"
                        className="project-sharing-input"
                        placeholder="Type to search users..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        onFocus={() => {
                          // Show dropdown if we already have search results
                          if (userSearchTerm && userSearchTerm.length >= 2 && availableUsers.length > 0) {
                            setShowUserDropdown(true);
                          }
                        }}
                        onBlur={() => {
                          // Close dropdown after a delay to allow click events
                          setTimeout(() => setShowUserDropdown(false), 200);
                        }}
                        onKeyDown={(e) => {
                          const filteredUsers = availableUsers.filter((user) => {
                            // Don't show the owner
                            if (cloud?.user_id === user.id) {
                              return false;
                            }
                            // Don't show users already shared
                            return !shareData?.shared_users.some(
                              (su) => su.user_id === user.id
                            );
                          });

                          if (e.key === 'Escape') {
                            // Close dropdown on Escape
                            setShowUserDropdown(false);
                            setHighlightedIndex(-1);
                          } else if (e.key === 'ArrowDown') {
                            // Navigate down
                            e.preventDefault();
                            if (showUserDropdown && filteredUsers.length > 0) {
                              setHighlightedIndex((prev) => 
                                prev < filteredUsers.length - 1 ? prev + 1 : prev
                              );
                            }
                          } else if (e.key === 'ArrowUp') {
                            // Navigate up
                            e.preventDefault();
                            if (showUserDropdown && filteredUsers.length > 0) {
                              setHighlightedIndex((prev) => 
                                prev > 0 ? prev - 1 : -1
                              );
                            }
                          } else if (e.key === 'Enter') {
                            // Select highlighted user
                            e.preventDefault();
                            if (highlightedIndex >= 0 && highlightedIndex < filteredUsers.length) {
                              const user = filteredUsers[highlightedIndex];
                              handleAddShare(user.id);
                              setUserSearchTerm('');
                              setShowUserDropdown(false);
                              setHighlightedIndex(-1);
                            }
                          }
                        }}
                      />
                      {/* Dropdown for user selection */}
                      {showUserDropdown && availableUsers.length > 0 && (
                        <div className="project-sharing-dropdown" ref={dropdownRef}>
                          {availableUsers
                            .filter((user) => {
                              // Don't show the owner
                              if (cloud?.user_id === user.id) {
                                return false;
                              }
                              // Don't show users already shared
                              return !shareData?.shared_users.some(
                                (su) => su.user_id === user.id
                              );
                            })
                            .map((user, index) => (
                              <div
                                key={user.id}
                                ref={index === highlightedIndex ? highlightedItemRef : null}
                                className={`project-sharing-dropdown-item ${
                                  index === highlightedIndex ? 'project-sharing-dropdown-item-highlighted' : ''
                                }`}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onMouseDown={(e) => {
                                  // Prevent blur from firing before click
                                  e.preventDefault();
                                }}
                                onClick={() => {
                                  handleAddShare(user.id);
                                  setUserSearchTerm('');
                                  setShowUserDropdown(false);
                                  setHighlightedIndex(-1);
                                }}
                              >
                                <div className="project-sharing-user-name">{user.name}</div>
                                <div className="project-sharing-user-email">{user.email}</div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* VM List Section */}
            <div className="vm-list-section">
              <div className="vm-list-header">
                <h2>Virtual Machines ({filteredVMs.length}{vms.length !== filteredVMs.length ? ` of ${vms.length}` : ''})</h2>
                <div className="vm-search-container">
                  <input
                    type="text"
                    className="vm-search-input"
                    placeholder="Search VMs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              {vms.length === 0 ? (
                <div className="empty-state">
                  <p>No VMs found in this project.</p>
                </div>
              ) : filteredVMs.length === 0 ? (
                <div className="empty-state">
                  <p>No VMs match your search.</p>
                </div>
              ) : (
                <div className="vm-table-container">
                  <table className="vm-table">
                    <thead>
                      <tr>
                        <th 
                          className="sortable" 
                          onClick={() => handleSort('vm_name')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSort('vm_name');
                            }
                          }}
                          aria-label="Sort by name"
                        >
                          Name
                          {sortColumn === 'vm_name' && (
                            <span className="sort-indicator">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th 
                          className="sortable" 
                          onClick={() => handleSort('status')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSort('status');
                            }
                          }}
                          aria-label="Sort by status"
                        >
                          Status
                          {sortColumn === 'status' && (
                            <span className="sort-indicator">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th 
                          className="sortable" 
                          onClick={() => handleSort('cores')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSort('cores');
                            }
                          }}
                          aria-label="Sort by cores"
                        >
                          Cores
                          {sortColumn === 'cores' && (
                            <span className="sort-indicator">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th 
                          className="sortable" 
                          onClick={() => handleSort('ram_mb')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSort('ram_mb');
                            }
                          }}
                          aria-label="Sort by RAM"
                        >
                          RAM
                          {sortColumn === 'ram_mb' && (
                            <span className="sort-indicator">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th 
                          className="sortable" 
                          onClick={() => handleSort('disk_gb')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSort('disk_gb');
                            }
                          }}
                          aria-label="Sort by disk"
                        >
                          Disk
                          {sortColumn === 'disk_gb' && (
                            <span className="sort-indicator">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th 
                          className="sortable" 
                          onClick={() => handleSort('created_by_user_name')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSort('created_by_user_name');
                            }
                          }}
                          aria-label="Sort by created by"
                        >
                          Created By
                          {sortColumn === 'created_by_user_name' && (
                            <span className="sort-indicator">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th 
                          className="sortable" 
                          onClick={() => handleSort('created_at')}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSort('created_at');
                            }
                          }}
                          aria-label="Sort by created at"
                        >
                          Created At
                          {sortColumn === 'created_at' && (
                            <span className="sort-indicator">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedVMs.map((vm) => (
                        <tr
                          key={vm.id}
                          className="vm-row"
                          onClick={() => navigate(`/projects/${projectId}/vms/${vm.vm_id}`)}
                        >
                          <td className="vm-name">{vm.vm_name || vm.vm_id}</td>
                          <td>
                            <span
                              className="vm-status"
                              style={{ color: getStatusColor(vm.status) }}
                            >
                              {vm.status || 'UNKNOWN'}
                            </span>
                          </td>
                          <td>{vm.cores ?? 'N/A'}</td>
                          <td>{vm.ram_mb ? formatBytes(vm.ram_mb) : 'N/A'}</td>
                          <td>{vm.disk_gb ? `${vm.disk_gb} GB` : 'N/A'}</td>
                          <td>{vm.created_by_user_name?.trim() || 'N/A'}</td>
                          <td>{formatDate(vm.created_at)}</td>
                          <td className="vm-actions" onClick={(e) => e.stopPropagation()}>
                            {shouldShowStartButton(vm.status) && (
                              <button
                                className="btn-action btn-start"
                                disabled
                                title="Start VM (coming soon)"
                                aria-label="Start VM"
                              >
                                <PlayIcon />
                              </button>
                            )}
                            {shouldShowStopPauseButtons(vm.status) && (
                              <>
                                <button
                                  className="btn-action btn-stop"
                                  disabled
                                  title="Stop VM (coming soon)"
                                  aria-label="Stop VM"
                                >
                                  <StopIcon />
                                </button>
                                <button
                                  className="btn-action btn-pause"
                                  disabled
                                  title="Pause VM (coming soon)"
                                  aria-label="Pause VM"
                                >
                                  <PauseIcon />
                                </button>
                              </>
                            )}
                            <button
                              className="btn-action btn-delete"
                              disabled
                              title="Delete VM (coming soon)"
                              aria-label="Delete VM"
                            >
                              <TrashIcon />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
        </div>
      </div>
    </Layout>
  );
};

